import { useQuery } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { signingApi } from '@/api/signingApi'
import { userGroupsApi } from '@/api/userGroupsApi'
import { useOptionalTenant } from '@/providers/TenantProvider'

// Optional tenant: the form renderer uses these and must still render with no
// providers mounted (its SSR smoke tests). With no tenant nothing is fetched.
//
// Rosters are read when a signature dialog OPENS, not before: most forms are
// filled without anyone ever reaching for "Sign as". `staleTime: 0` because an
// eligibility that changed a minute ago (someone added to QA Review) should
// show the next time the picker opens, not five minutes later.

/**
 * The "Sign as:" / "Remove as:" roster for one action on a submission, with
 * the field's (or form's) group rule and permission applied by the server.
 */
export function useSubmissionSigners(
  submissionId: string | null,
  query: { action: 'sign' | 'remove' | 'complete'; fieldId?: string },
  enabled: boolean,
) {
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  return useQuery({
    queryKey: ['submission-signers', tenantId, submissionId, query.action, query.fieldId ?? null],
    enabled: enabled && !!tenantId && !!submissionId,
    queryFn: () => formsApi.listSubmissionSigners(submissionId as string, query),
    staleTime: 0,
  })
}

/**
 * The tenant roster, eligibility by one permission — for signatures with no
 * form behind them (version-control events, process steps).
 */
export function useTenantSigners(permission: string | undefined, enabled: boolean) {
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  return useQuery({
    queryKey: ['tenant-signers', tenantId, permission ?? null],
    enabled: enabled && !!tenantId,
    queryFn: () => signingApi.listSigners(permission),
    staleTime: 0,
  })
}

/** "View Allowed Signatories" for one signature field. */
export function useAllowedSignatories(
  submissionId: string | null,
  fieldId: string | null,
  enabled: boolean,
) {
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  return useQuery({
    queryKey: ['allowed-signatories', tenantId, submissionId, fieldId],
    enabled: enabled && !!tenantId && !!submissionId && !!fieldId,
    queryFn: () => formsApi.getAllowedSignatories(submissionId as string, fieldId as string),
    staleTime: 0,
  })
}

/** The tenant's user groups — the builder picker and the admin tab. */
export function useUserGroups(enabled = true) {
  const tenantId = useOptionalTenant()?.selectedTenantId ?? null
  return useQuery({
    queryKey: ['user-groups', tenantId],
    enabled: enabled && !!tenantId,
    queryFn: () => userGroupsApi.list(),
  })
}
